/**
 * An ORDINARY automatic Project Summary run on Stage 1 (import-complete,
 * manual-entry debounce, notes resume, config recovery, the failure retry):
 * whether it may still select the Summary tab when it lands.
 *
 * The Summary run is not a pure function of what the operator had when the
 * timer fired. It can read evidence out of storage, fill missing RFQ identity
 * and append assessor notes, and each of those changes the evidence
 * fingerprint. So the run is judged by what IT wrote, not by the evidence it
 * was scheduled against (C21).
 *
 * Two independent authorities decide whether the landing may take the view:
 *
 *   1. The navigation epoch. STOP, an accepted menu or history navigation and
 *      the Data Entry pill advance it. If it moved after the run began, the
 *      operator's later choice wins and the Summary is not painted over it.
 *   2. The evidence witness. The witness follows the evidence the RUN ITSELF
 *      has produced: it starts from the evidence on screen when the run began
 *      and advances only by the run's own writes, each applied to the witness's
 *      own copy, never read back from the live form. Every fingerprint the
 *      screen commits while the run works is recorded, and a committed
 *      fingerprint the run's writes do not explain was written by someone else.
 *      That withholds the tab even if the operator then reverts the edit, and a
 *      later write by the run that happened to build on the operator's edit
 *      cannot bless it, because the witness's copy never contained the edit.
 *      The run's final saved fingerprint must equal the end of that chain.
 *
 * Owned writes are reported where they actually happen: each evidence
 * checkpoint the preparation adopts (one per file), the RFQ identity merge and
 * the assessor-notes merge. Nothing here depends on which control made an
 * operator edit.
 *
 * This was the Summary half of the Summary -> Risk handoff
 * (attended-summary-handoff.ts) until the old pipeline's removal
 * (docs/old-pipeline-removal/PLAN.md); the Risk request it also armed is gone.
 */
import { isAttendedHandoffPage } from '../utils/page-routing';
import { buildProjectSummaryEvidenceFingerprint } from '../utils/project-summary-evidence';

interface RefLike<T> { current: T }
type Form = Record<string, unknown>;

/**
 * The lanes the Project Summary evidence fingerprint is computed from. Not the
 * preprocessed-file lane: the fingerprint does not read it (project-summary-evidence.ts).
 */
export interface SummaryEvidenceLanes {
    documents?: unknown;
    photos?: unknown;
    videoTranscripts?: unknown;
}

function pickLanes(source: SummaryEvidenceLanes | null | undefined): SummaryEvidenceLanes {
    return {
        documents: source?.documents,
        photos: source?.photos,
        videoTranscripts: source?.videoTranscripts,
    };
}

/** What one automatic Summary run has seen and written. */
export interface AttendedSummaryWitness {
    readonly epoch: number;
    /** The evidence on screen when the run began, advanced only by the run's own writes. */
    ownedForm: Form;
    ownedEvidence: SummaryEvidenceLanes;
    /** The starting fingerprint plus the fingerprint after each owned write. */
    readonly allowed: Set<string>;
    /** Every distinct fingerprint the screen committed while the run worked. */
    readonly observed: Set<string>;
    settled: boolean;
}

function ownedFingerprint(witness: AttendedSummaryWitness): string {
    return buildProjectSummaryEvidenceFingerprint({ formData: witness.ownedForm, ...witness.ownedEvidence });
}

export interface AttendedSummaryRunRefs {
    /** Advanced by every run that begins and by every operator choice that revokes. */
    epoch: RefLike<number>;
    witness: RefLike<AttendedSummaryWitness | null>;
    /** The live form and live evidence lanes, read once when a run begins. */
    form: RefLike<Form | null | undefined>;
    evidence: RefLike<SummaryEvidenceLanes | null | undefined>;
    /** True while any Project Summary run holds the busy lock. */
    summarizing: RefLike<boolean>;
    currentFingerprint: RefLike<string>;
    page: RefLike<unknown>;
    stage: RefLike<unknown>;
    loading: RefLike<boolean>;
    halted: RefLike<boolean>;
    review: RefLike<boolean>;
}

export interface AttendedAutomaticSummaryRunOptions {
    silent: true;
    autoAdvanceOnReady: true;
    automatic: true;
    /**
     * The run has just written the form itself. `apply` is the same pure
     * transform it applied to the live form; the witness applies it to ITS copy.
     */
    onOwnedFormChange: (apply: (form: Form) => Form) => void;
    /**
     * Read-only: is the operator's intent still the one this run started with,
     * and are they still on Stage 1, where the Summary tab is? Used by the Summary
     * hook to decide whether a TERMINAL failure may select the Summary tab.
     *
     * Two things can make it false. The epoch moving (Stage 1 pill, Back, a
     * menu trip, STOP) is the operator choosing Data Entry or elsewhere. But a
     * pill to a later stage does not advance that epoch, so the live page, stage
     * and load state are checked too: the Summary tab is never selected while
     * the operator is on another stage or another page. A passive monitoring page
     * (Developer Tools, AI Models) is not "another page": the trip does not
     * advance the epoch, and the Summary may complete and paint while the
     * operator watches there.
     */
    isViewIntentCurrent: () => boolean;
    /** The run saved its Summary: may it select the Summary tab? */
    onSummarySaved: (saved: { summaryEvidenceFingerprint?: string }) => boolean;
}

/** The operator chose something else: a Summary run that is still working loses its right to paint. */
export function revokeAttendedSummary(refs: Pick<AttendedSummaryRunRefs, 'epoch'>): void {
    refs.epoch.current += 1;
}

/**
 * Claim the view for one automatic Summary and return its run options, or null
 * when no run should start.
 *
 * A second trigger while a Summary is in flight returns null WITHOUT touching
 * the epoch: the run that is already going owns the view, and bumping the
 * epoch would revoke it.
 */
export function beginAttendedAutomaticSummary(
    refs: AttendedSummaryRunRefs,
    scheduledEvidenceFingerprint: string | null = null,
): AttendedAutomaticSummaryRunOptions | null {
    if (refs.summarizing.current) return null;
    const startFingerprint = refs.currentFingerprint.current;
    if (scheduledEvidenceFingerprint && scheduledEvidenceFingerprint !== startFingerprint) {
        return null;
    }

    const epoch = ++refs.epoch.current;
    const witness: AttendedSummaryWitness = {
        epoch,
        ownedForm: (refs.form.current || {}) as Form,
        ownedEvidence: pickLanes(refs.evidence.current),
        allowed: new Set(),
        observed: new Set(),
        settled: false,
    };
    for (const fingerprint of [startFingerprint, ownedFingerprint(witness)]) {
        if (fingerprint) { witness.allowed.add(fingerprint); witness.observed.add(fingerprint); }
    }
    refs.witness.current = witness;

    return {
        silent: true,
        autoAdvanceOnReady: true,
        automatic: true,
        onOwnedFormChange: (apply) => {
            if (witness.settled || typeof apply !== 'function') return;
            witness.ownedForm = apply(witness.ownedForm);
            witness.allowed.add(ownedFingerprint(witness));
        },
        isViewIntentCurrent: () => (
            refs.epoch.current === epoch
            && refs.halted.current !== true
            && refs.review.current !== true
            && isAttendedHandoffPage(refs.page.current)
            && Number(refs.stage.current) === 1
            && refs.loading.current !== true
        ),
        onSummarySaved: ({ summaryEvidenceFingerprint } = {}) => {
            witness.settled = true;
            if (refs.witness.current === witness) refs.witness.current = null;

            const savedFingerprint = typeof summaryEvidenceFingerprint === 'string'
                ? summaryEvidenceFingerprint.trim()
                : '';
            // The return value is the Summary hook's permission to select the
            // Summary tab, so it is false whenever the operator's intent no
            // longer covers it.
            if (!savedFingerprint) return false;
            // Any revocation (STOP, accepted navigation, the Data Entry pill)
            // advanced the epoch after this run began.
            if (refs.epoch.current !== epoch) return false;
            if (!isAttendedHandoffPage(refs.page.current)
                || Number(refs.stage.current) !== 1
                || refs.loading.current === true
                || refs.halted.current === true
                || refs.review.current === true) return false;
            // The Summary is built from the LIVE form, so its fingerprint
            // proves nothing about who wrote what it contains. It is accepted
            // only when it is exactly where the run's own writes lead.
            if (savedFingerprint !== ownedFingerprint(witness)) return false;
            for (const seen of witness.observed) {
                if (!witness.allowed.has(seen)) return false;
            }
            return true;
        },
    };
}

/**
 * Record the evidence fingerprint the screen has just committed. Called from
 * a commit-time effect so it sees every state the operator could see, whoever
 * wrote it. The comparison against what the run owns is deferred to the save,
 * by which time the run has reported everything it wrote.
 */
export function observeAttendedSummaryEvidence(
    refs: Pick<AttendedSummaryRunRefs, 'witness' | 'summarizing'>,
    fingerprint: string,
): void {
    const witness = refs.witness.current;
    if (!witness || witness.settled || !refs.summarizing.current) return;
    if (typeof fingerprint === 'string' && fingerprint) witness.observed.add(fingerprint);
}

/**
 * The evidence preparation has just adopted one verified checkpoint (one per
 * file). Called from App's `adoptEvidence`, synchronously with the setters, so
 * the checkpoint's fingerprint is owned before any render can show it.
 *
 * `appendedNotes` is the text the checkpoint appended to Inspector Notes (a new
 * transcript). It is applied to the witness's own notes, not copied from the
 * live form, so an operator edit made before the checkpoint stays external.
 */
export function registerAdoptedEvidence(
    refs: Pick<AttendedSummaryRunRefs, 'witness' | 'summarizing'>,
    adopted: SummaryEvidenceLanes,
    { appendedNotes = '' }: { appendedNotes?: string } = {},
): void {
    const witness = refs.witness.current;
    if (!witness || witness.settled || !refs.summarizing.current) return;
    witness.ownedEvidence = pickLanes(adopted);
    if (appendedNotes) {
        witness.ownedForm = { ...witness.ownedForm, notes: String(witness.ownedForm.notes || '') + appendedNotes };
    }
    witness.allowed.add(ownedFingerprint(witness));
}
